import React, { useState, useEffect, useRef } from 'react';
import { VirtualTrade } from '../types';

interface VirtualTradingPanelProps {
  isActive: boolean;
  currentPrice: number;
  onBuy: (quantity: number) => void;
  onSell: (quantity: number) => void;
  onMinimize?: () => void;
  onClosePosition: (tradeId: string) => void;
  trades: VirtualTrade[];
  balance: number;
  equity: number;
  timezone: string;
}

const VirtualTradingPanel: React.FC<VirtualTradingPanelProps> = ({
  isActive,
  currentPrice,
  onBuy,
  onSell,
  onMinimize,
  onClosePosition,
  trades,
  balance,
  equity,
  timezone
}) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [isRiskBased, setIsRiskBased] = useState<boolean>(false);
  const [riskPercentage, setRiskPercentage] = useState<number>(1); 
  
  // Draggable Logic State
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const startOffsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    startOffsetRef.current = { ...offset };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        setOffset({
            x: startOffsetRef.current.x + dx,
            y: startOffsetRef.current.y + dy
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    if (isDragging) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isActive) return null;

  const openTrades = trades.filter(t => t.status === 'OPEN');
  const pnl = equity - balance;
  const pnlPercent = balance > 0 ? (pnl / balance) * 100 : 0;

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val) && val > 0) {
      setQuantity(val);
    }
  };

  const calculateMaxQty = () => {
      if (currentPrice > 0) {
          const maxQ = Math.floor((balance * 0.99) / currentPrice);
          setQuantity(maxQ > 0 ? maxQ : 1);
      }
  };

  return (
    <div 
        className="fixed bottom-20 right-4 w-80 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-30 animate-slide-up"
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
    >
      
      {/* Header / Account Info - Draggable Area */}
      <div 
        className="p-4 bg-slate-800/50 border-b border-slate-700 cursor-move select-none active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        title="Drag to move"
      >
        <div className="flex items-start justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2 pointer-events-none">
            <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
            Paper Trading (Replay)
          </h3>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMinimize?.();
            }}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors"
            title="Minimize"
            aria-label="Minimize paper trading"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3 pointer-events-none">
          <div>
            <span className="text-xs text-slate-500 block">Account Equity</span>
            <span className="text-lg font-mono font-bold text-white">${equity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-500 block">Total P&L</span>
            <span className={`text-lg font-mono font-bold ${pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {pnl >= 0 ? '+' : ''}{pnl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Order Controls */}
      <div className="p-4 space-y-3">
          {/* Quantity Input */}
          <div className="flex items-end gap-2">
              <div className="flex-1">
                  <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Quantity</label>
                  <input 
                    type="number" 
                    value={quantity}
                    onChange={handleQuantityChange}
                    min="1"
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-white text-sm font-mono focus:ring-1 focus:ring-blue-500 outline-none"
                  />
              </div>
              <button 
                onClick={calculateMaxQty}
                className="bg-slate-800 hover:bg-slate-700 text-xs text-blue-400 font-medium px-2 py-2 rounded border border-slate-700 transition-colors h-[34px]"
                title="Calculate Max Buy"
              >
                  Max
              </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
            onClick={() => onBuy(quantity)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg font-bold transition-all active:scale-95 flex flex-col items-center"
            >
            <span>BUY</span>
            <span className="text-[10px] font-mono opacity-80">{currentPrice.toFixed(2)}</span>
            </button>
            <button
            onClick={() => onSell(quantity)}
            className="bg-rose-600 hover:bg-rose-500 text-white py-3 rounded-lg font-bold transition-all active:scale-95 flex flex-col items-center"
            >
            <span>SELL</span>
            <span className="text-[10px] font-mono opacity-80">{currentPrice.toFixed(2)}</span>
            </button>
        </div>
      </div>

      {/* Positions List */}
      <div className="max-h-48 overflow-y-auto border-t border-slate-700 bg-slate-950/50">
        {openTrades.length === 0 ? (
          <div className="p-4 text-center text-slate-500 text-xs italic">
            No open positions.
          </div>
        ) : (
          openTrades.map(trade => {
            const tradePnl = (currentPrice - trade.entryPrice) * trade.quantity * (trade.type === 'BUY' ? 1 : -1);
            return (
              <div key={trade.id} className="p-3 border-b border-slate-800 flex justify-between items-center group">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-1.5 rounded ${trade.type === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                      {trade.type}
                    </span>
                    <span className="text-xs font-mono text-slate-300">
                        {trade.quantity} @ {trade.entryPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {new Date(trade.entryTime).toLocaleTimeString('en-US', {
                      timeZone: timezone,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-sm font-bold ${tradePnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {tradePnl >= 0 ? '+' : ''}{tradePnl.toFixed(2)}
                  </span>
                  <button 
                    onClick={() => onClosePosition(trade.id)}
                    className="text-slate-500 hover:text-white p-1 rounded hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-all"
                    title="Close Position"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VirtualTradingPanel;